import { useState, useEffect, useRef } from "react";
import { useAuth } from "@clerk/react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Copy, Check, Users, ShieldAlert, Loader2, Plus, X } from "lucide-react";
import { Link } from "wouter";
import { handleApiError } from "@/lib/handleApiError";
import {
  approvalCleared,
  approvalStageLabel,
  type ApprovalStageId,
  type ApprovalStatusLight,
} from "@/lib/workflowStages";

interface Counterparty {
  id: number;
  name: string;
  firm: string | null;
  role: string;
  email: string;
}

interface SendExternalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: number;
  onSuccess: () => void;
  /**
   * Detail page URL prefix used when the dialog deep-links to the parent
   * page's approval tab. Defaults to `/contracts`; the term-sheet detail
   * page passes `/term-sheets`.
   */
  basePath?: string;
}

type RecipientRole = "coach" | "agent" | "attorney" | "other";

interface RecipientRow {
  recipientName: string;
  recipientEmail: string;
  recipientRole: RecipientRole;
  counterpartyId?: number;
}

interface SentLink {
  recipientName: string;
  recipientEmail: string;
  shareUrl: string;
  emailSent: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function blankRecipient(): RecipientRow {
  return { recipientName: "", recipientEmail: "", recipientRole: "agent" };
}

export function SendExternalDialog({
  open,
  onOpenChange,
  contractId,
  onSuccess,
  basePath = "/contracts",
}: SendExternalDialogProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [isPending, setIsPending] = useState(false);
  const [sentLinks, setSentLinks] = useState<SentLink[] | null>(null);
  const [anyEmailFailed, setAnyEmailFailed] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [coverTemplates, setCoverTemplates] = useState<{ id: number; label: string; body: string }[]>([]);

  const [recipients, setRecipients] = useState<RecipientRow[]>([blankRecipient()]);
  const [note, setNote] = useState("");
  const [coverNote, setCoverNote] = useState("");
  const [responseByDate, setResponseByDate] = useState("");
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatusLight | null>(null);
  const [approvalLoading, setApprovalLoading] = useState(false);

  const applyApproval = (data: { approval: { status: string; currentStage: string } | null }) => {
    if (data.approval) {
      setApprovalStatus({
        hasApproval: true,
        status: data.approval.status as ApprovalStatusLight["status"],
        currentStage: data.approval.currentStage as ApprovalStageId,
      });
    } else {
      setApprovalStatus({ hasApproval: false, status: "none", currentStage: null });
    }
  };

  // Initial load: counterparties, cover templates, and a first approval check.
  useEffect(() => {
    if (!open) return;
    setApprovalLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const [cpRes, tmplRes, approvalRes] = await Promise.all([
          fetch(`/api/counterparties`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`/api/cover-note-templates`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`/api/contracts/${contractId}/approval`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (cpRes.ok) setCounterparties(await cpRes.json());
        if (tmplRes.ok) setCoverTemplates(await tmplRes.json());
        if (approvalRes.ok) {
          applyApproval(await approvalRes.json());
        }
      } catch { /* ignore */ }
      finally {
        setApprovalLoading(false);
      }
    })();
  }, [open, getToken, contractId]);

  const isBlocked = approvalStatus !== null && !approvalCleared(approvalStatus);

  // Change 2 — approval auto-advance: while the gate is BLOCKED and the dialog
  // is open (and the link hasn't been generated yet), poll the approval status
  // every 5s so the send UI unlocks automatically when the last internal
  // approval lands — no manual browser refresh required. Polling stops as soon
  // as the gate clears, the link is created, or the dialog closes/unmounts.
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const shouldPoll = open && !sentLinks && isBlocked;
    if (!shouldPoll) return;
    let cancelled = false;
    pollingRef.current = setInterval(async () => {
      try {
        const token = await getToken();
        const res = await fetch(`/api/contracts/${contractId}/approval`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) applyApproval(await res.json());
      } catch { /* ignore transient errors; keep polling */ }
    }, 5000);
    return () => {
      cancelled = true;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [open, sentLinks, isBlocked, getToken, contractId]);

  const blockedReason: string = (() => {
    if (!approvalStatus || !isBlocked) return "";
    if (!approvalStatus.hasApproval) {
      return "This contract has not been submitted for internal approval yet. Finance, HR, Legal and AD must sign off before it can be sent externally.";
    }
    if (approvalStatus.status === "rejected") {
      return "The current internal approval was rejected. Address the comments and re-submit before sending externally.";
    }
    if (approvalStatus.status === "returned") {
      return "An internal reviewer requested changes. Address the comments and re-submit before sending externally.";
    }
    const label = approvalStageLabel(approvalStatus.currentStage);
    return `Internal approvals are still in progress — waiting on ${label}. All pre-signature stages must clear before this contract can be sent externally.`;
  })();

  const updateRecipient = (idx: number, patch: Partial<RecipientRow>) => {
    setRecipients((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRecipient = () => {
    setRecipients((prev) => (prev.length >= 10 ? prev : [...prev, blankRecipient()]));
  };

  const removeRecipient = (idx: number) => {
    setRecipients((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const handleCounterpartySelect = (idx: number, id: string) => {
    if (id && id !== "manual") {
      const cp = counterparties.find((c) => String(c.id) === id);
      if (cp) {
        updateRecipient(idx, {
          recipientName: cp.name,
          recipientEmail: cp.email,
          recipientRole: cp.role as RecipientRole,
          counterpartyId: cp.id,
        });
      }
    } else {
      updateRecipient(idx, { counterpartyId: undefined });
    }
  };

  const validRecipients = recipients.filter(
    (r) => r.recipientName.trim() !== "" && EMAIL_RE.test(r.recipientEmail.trim()),
  );
  const canSend = validRecipients.length > 0;

  const handleClose = () => {
    if (!isPending) {
      onOpenChange(false);
      setSentLinks(null);
      setAnyEmailFailed(false);
      setCopiedIdx(null);
      setRecipients([blankRecipient()]);
      setNote("");
      setCoverNote("");
      setResponseByDate("");
      setApprovalStatus(null);
    }
  };

  const handleSend = async () => {
    if (!canSend) {
      toast({ title: "Missing fields", description: "Add at least one recipient with a name and valid email.", variant: "destructive" });
      return;
    }
    setIsPending(true);
    try {
      const token = await getToken();
      const body: Record<string, unknown> = {
        recipients: validRecipients.map((r) => ({
          recipientName: r.recipientName.trim(),
          recipientEmail: r.recipientEmail.trim(),
          recipientRole: r.recipientRole,
          ...(r.counterpartyId ? { counterpartyId: r.counterpartyId } : {}),
        })),
        note: note.trim() || undefined,
        coverNote: coverNote.trim() || undefined,
        responseByDate: responseByDate || undefined,
      };
      const res = await fetch(`/api/contracts/${contractId}/send-external`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        // Bubble through handleApiError so the centralized known-error map
        // (e.g. internal_approvals_pending) renders a consistent toast and
        // we don't get a generic "Error" surface.
        handleApiError(toast, { status: res.status, data: errBody }, {
          fallbackTitle: "Could not send contract",
        });
        return;
      }
      const data = await res.json() as {
        emailSent?: boolean;
        recipients?: Array<{
          recipientName: string;
          recipientEmail: string;
          shareUrl: string;
          emailSent: boolean;
        }>;
        accessToken?: string;
      };

      const links: SentLink[] = (data.recipients ?? []).map((r) => ({
        recipientName: r.recipientName,
        recipientEmail: r.recipientEmail,
        // shareUrl from the API is a path; render an absolute URL for copying.
        shareUrl: `${window.location.origin}${r.shareUrl}`,
        emailSent: r.emailSent,
      }));
      setSentLinks(links);
      const aggregateFailed = data.emailSent === false;
      setAnyEmailFailed(aggregateFailed);
      onSuccess();
      if (aggregateFailed) {
        toast({
          title: "Links created — some emails not sent",
          description: "We couldn't email one or more recipients. Copy their link(s) below and send directly.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Sent for negotiation",
          description: `Contract sent to ${links.length} recipient${links.length === 1 ? "" : "s"}. The holder is now with the counterparty.`,
        });
      }
    } catch (err: unknown) {
      handleApiError(toast, err, { fallbackTitle: "Could not send contract" });
    } finally {
      setIsPending(false);
    }
  };

  const handleCopy = async (idx: number, url: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send to Coach / Representation</DialogTitle>
          <DialogDescription>
            Generate a unique shareable link for each recipient and move this contract into "Out for Negotiation." The holder indicator will flip to the counterparty.
          </DialogDescription>
        </DialogHeader>

        {sentLinks ? (
          <div className="space-y-4">
            {anyEmailFailed && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="send-external-email-warning">
                ⚠ The notification email could not be sent — share this link directly.
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              The contract has been sent. Share each access link with its recipient:
            </p>
            <div className="space-y-3">
              {sentLinks.map((link, idx) => (
                <div key={idx} className="space-y-1" data-testid="send-external-recipient-link">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{ color: "#0F1E3D" }}>
                      {link.recipientName}
                    </span>
                    {!link.emailSent && (
                      <span className="text-xs text-amber-700">email not sent</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input value={link.shareUrl} readOnly className="text-xs font-mono" />
                    <Button variant="outline" size="icon" onClick={() => handleCopy(idx, link.shareUrl)}>
                      {copiedIdx === idx ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Use "Mark Received" in the Negotiation Rounds timeline when a response arrives.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {approvalLoading && (
              <div
                className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex items-center gap-2"
                data-testid="send-external-approval-loading"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Checking internal approval status…
              </div>
            )}
            {!approvalLoading && isBlocked && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900 space-y-2"
                role="alert"
                data-testid="send-external-blocked"
              >
                <div className="flex items-start gap-2">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div className="flex-1 space-y-1">
                    <div className="font-semibold">Internal approvals required</div>
                    <p className="text-xs leading-relaxed">{blockedReason}</p>
                    <Link
                      href={`${basePath}/${contractId}#approval`}
                      onClick={() => onOpenChange(false)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
                      data-testid="send-external-blocked-link"
                    >
                      Open the Approvals tab →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>
                  <Users className="h-3.5 w-3.5 inline mr-1.5 text-muted-foreground" />
                  Recipients <span className="text-destructive">*</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addRecipient}
                  disabled={recipients.length >= 10}
                  data-testid="send-external-add-recipient"
                  className="h-7 text-xs"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add recipient
                </Button>
              </div>

              {recipients.map((r, idx) => (
                <div
                  key={idx}
                  className="rounded-md border p-3 space-y-2"
                  style={{ borderColor: "#0F1E3D22" }}
                  data-testid="send-external-recipient-row"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Recipient {idx + 1}</span>
                    {recipients.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeRecipient(idx)}
                        data-testid="send-external-remove-recipient"
                        aria-label="Remove recipient"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  {counterparties.length > 0 && (
                    <Select
                      value={r.counterpartyId ? String(r.counterpartyId) : ""}
                      onValueChange={(id) => handleCounterpartySelect(idx, id)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="From directory (optional)…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">— Enter manually —</SelectItem>
                        {counterparties.map((cp) => (
                          <SelectItem key={cp.id} value={String(cp.id)}>
                            {cp.name}{cp.firm ? ` · ${cp.firm}` : ""} <span className="text-muted-foreground capitalize">({cp.role})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  <Input
                    placeholder="Recipient name"
                    value={r.recipientName}
                    onChange={(e) => updateRecipient(idx, { recipientName: e.target.value })}
                    data-testid="send-external-recipient-name"
                  />
                  <Input
                    type="email"
                    placeholder="email@agencyname.com"
                    value={r.recipientEmail}
                    onChange={(e) => updateRecipient(idx, { recipientEmail: e.target.value })}
                    data-testid="send-external-recipient-email"
                  />
                  <Select
                    value={r.recipientRole}
                    onValueChange={(v) => updateRecipient(idx, { recipientRole: v as RecipientRole })}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="coach">Coach</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                      <SelectItem value="attorney">Attorney</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="response-by">Response By Date (optional)</Label>
              <Input
                id="response-by"
                type="date"
                value={responseByDate}
                onChange={(e) => setResponseByDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="cover-note">Cover Note (optional)</Label>
                {coverTemplates.length > 0 && (
                  <Select onValueChange={(body) => setCoverNote(body)}>
                    <SelectTrigger className="h-6 text-xs w-[160px]">
                      <SelectValue placeholder="Use template…" />
                    </SelectTrigger>
                    <SelectContent>
                      {coverTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.body}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Textarea
                id="cover-note"
                placeholder="Cover note to include with the send (visible to counterparty)…"
                rows={3}
                value={coverNote}
                onChange={(e) => setCoverNote(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="send-note">Internal Note (optional)</Label>
              <Textarea
                id="send-note"
                placeholder="Internal context for your team (not shared)…"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {sentLinks ? "Close" : "Cancel"}
          </Button>
          {!sentLinks && (
            <Button
              onClick={handleSend}
              disabled={isPending || isBlocked || approvalLoading || !canSend}
              data-testid="send-external-submit"
              style={{ backgroundColor: "#0F1E3D", color: "#fff" }}
              title={isBlocked ? "Internal approvals must clear before sending externally" : undefined}
            >
              <Send className="h-4 w-4 mr-2" />
              {isPending ? "Sending…" : "Send & Get Links"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
